import React, { useState } from 'react';

import LibbyMark from './LibbyMark';

import { NEWSLETTER_API_URL } from '../config';

const CHIPS = ['📬 Weekly updates', '📚 Amazon book releases', '🎁 Merchandise & special offers'];

async function parseJsonSafe(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function formatApiError(data, res) {
  if (typeof data?.message === 'string' && data.message) return data.message;

  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors)
      .flat()
      .filter(entry => typeof entry === 'string' && entry.trim());
    if (messages.length) return messages.join(' ');
  }

  if (typeof data?.title === 'string' && data.title) return data.title;

  if (typeof data === 'string' && data) return data;

  if (res.ok) return 'Successfully subscribed!';

  return 'Something went wrong. Please try again.';
}

export default function NewsletterSignup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [status, setStatus] = useState('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setFeedback('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus('error');
      setFeedback('Please enter your email address.');
      return;
    }

    if (!optIn) {
      setStatus('error');
      setFeedback('Please check the box to join the email list.');
      return;
    }

    if (!NEWSLETTER_API_URL) {
      setStatus('error');
      setFeedback('Newsletter signup is not configured. Please try again later.');
      return;
    }

    setStatus('loading');

    try {
      const res = await fetch(`${NEWSLETTER_API_URL}/api/signups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: trimmedEmail,
          optIn: true,
        }),
      });

      const data = await parseJsonSafe(res);
      const msg = formatApiError(data, res);

      if (res.ok) {
        setStatus('success');
        setFeedback(
          data.emailSent === false
            ? 'Successfully subscribed! Your signup was saved, but we could not send the welcome email right now.'
            : 'Successfully subscribed!'
        );
        setName('');
        setEmail('');
        setOptIn(true);
        return;
      }

      setStatus('error');
      setFeedback(msg);
    } catch (err) {
      setStatus('error');
      const apiHint = NEWSLETTER_API_URL ? ` (${NEWSLETTER_API_URL})` : '';
      const detail =
        err instanceof TypeError
          ? ` Network or CORS error — often means the newsletter API is not deployed${apiHint}. Open ${NEWSLETTER_API_URL || 'the API URL'}/health in a browser; you should see JSON, not an IIS 404 page.`
          : '';
      setFeedback(`We could not reach the newsletter server.${detail}`);
    }
  };

  return (
    <section className="newsletter-section" id="newsletter" aria-labelledby="newsletter-heading">
      <div className="newsletter-inner">
        <div className="newsletter-copy">
          <p className="newsletter-emoji" aria-hidden>
            🌟
          </p>
          <h2 id="newsletter-heading" className="newsletter-title">
            Join the weekly newsletter
          </h2>
          <p className="newsletter-desc">
            Get a cheerful weekly note from Libby&apos;s world — new books on Amazon, merchandise previews, and what&apos;s
            coming next. No spam, just roar-worthy news.
          </p>
          <ul className="newsletter-chips" aria-label="What you will hear about">
            {CHIPS.map(c => (
              <li key={c} className="newsletter-chip">
                {c}
              </li>
            ))}
          </ul>
        </div>
        <form className="newsletter-form" onSubmit={handleSubmit} noValidate>
          <label className="newsletter-label">
            <span className="visually-hidden">Your name</span>
            <input
              type="text"
              name="name"
              autoComplete="name"
              className="newsletter-input newsletter-input--full"
              placeholder="Your name (optional)"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={status === 'loading'}
            />
          </label>
          <div className="newsletter-row">
            <label className="newsletter-label newsletter-label--grow">
              <span className="visually-hidden">Email address</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                className="newsletter-input"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={status === 'loading'}
                aria-invalid={status === 'error'}
                aria-describedby={feedback ? 'newsletter-feedback' : undefined}
              />
            </label>
            <button type="submit" className="newsletter-submit" disabled={status === 'loading'}>
              {status === 'loading' ? (
                'Sending…'
              ) : (
                <>
                  Join the list
                  <LibbyMark size={20} variant="dark" className="newsletter-submit-mark" alt="" aria-hidden />
                </>
              )}
            </button>
          </div>
          <label className="newsletter-optin">
            <input
              type="checkbox"
              name="optIn"
              checked={optIn}
              onChange={e => setOptIn(e.target.checked)}
              disabled={status === 'loading'}
            />
            <span>Yes, send me Libby newsletter updates</span>
          </label>
          {feedback ? (
            <p
              id="newsletter-feedback"
              className={`newsletter-feedback${status === 'success' ? ' newsletter-feedback--success' : ''}${status === 'error' ? ' newsletter-feedback--error' : ''}`}
              role={status === 'error' ? 'alert' : 'status'}
            >
              {feedback}
            </p>
          ) : (
            <p className="newsletter-privacy">
              We respect your privacy.{' '}
              <a href="/unsubscribe.html">Unsubscribe anytime</a>.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
